// Environment variable validator utility
// This utility checks for required API keys and logs warnings when they're missing
// but allows the application to continue loading

// Group environment variables by service
type ServiceEnvVars = {
  [service: string]: {
    description: string;
    variables: {
      [key: string]: {
        required: boolean;
        description: string;
      };
    };
  };
};

// Define environment variables by service
const environmentVariables: ServiceEnvVars = {
  stripe: {
    description: "Stripe payment processing",
    variables: {
      "VITE_STRIPE_PUBLISHABLE_KEY": {
        required: true,
        description: "Public key for Stripe integration",
      },
      "STRIPE_SECRET_KEY": {
        required: true,
        description: "Secret key for Stripe API calls",
      }
    }
  },
  google: {
    description: "Google authentication and services",
    variables: {
      "GOOGLE_CLIENT_ID": {
        required: true,
        description: "Google OAuth client ID",
      },
      "GOOGLE_CLIENT_SECRET": {
        required: true,
        description: "Google OAuth client secret",
      },
      "GOOGLE_SERVICE_ACCOUNT_EMAIL": {
        required: false,
        description: "Google service account email for Google Sheets",
      },
      "GOOGLE_PRIVATE_KEY": {
        required: false,
        description: "Google service account private key",
      },
      "GOOGLE_SHEET_ID": {
        required: false,
        description: "Google Sheet ID for data storage",
      },
      "GOOGLE_CALLBACK_URL": {
        required: true,
        description: "Google OAuth callback URL",
      },
      "VITE_GEMINI_API_KEY": {
        required: false,
        description: "Google Gemini API Key for AI features",
      },
    }
  },
  schwab: {
    description: "Charles Schwab integration",
    variables: {
      "VITE_SCHWAB_API_KEY": {
        required: false,
        description: "Schwab API key for integration",
      },
      "VITE_SCHWAB_APP_SECRET": {
        required: false,
        description: "Schwab app secret for authentication",
      },
      "VITE_SCHWAB_CALLBACK_URL": {
        required: false,
        description: "Schwab OAuth callback URL",
      },
    }
  },
  tradier: {
    description: "Tradier brokerage integration",
    variables: {
      "VITE_TRADIER_TOKEN": {
        required: false,
        description: "Tradier API token",
      },
      "VITE_TRADIER_ACCOUNT_ID": {
        required: false,
        description: "Tradier account ID",
      },
    }
  },
  email: {
    description: "Email services",
    variables: {
      "RESEND_API_KEY": {
        required: false,
        description: "Resend email service API key",
      },
      "VITE_RESEND_API_KEY": {
        required: false,
        description: "Resend email service API key for frontend",
      },
      "VITE_VERIFIED_FROM_EMAIL": {
        required: false,
        description: "Verified email address for sending emails",
      },
    }
  },
  application: {
    description: "General application settings",
    variables: {
      "VITE_FRONTEND_URL": {
        required: false,
        description: "Frontend application URL",
      },
      "FRONTEND_URL": {
        required: false,
        description: "Frontend application URL (backend version)",
      },
      "JWT_SECRET": {
        required: true,
        description: "Secret for JWT token generation and validation",
      },
      "VITE_BACKEND_PORT": {
        required: false,
        description: "Backend server port",
      },
    }
  },
};

// Interface to return validation results
interface ValidationResult {
  isValid: boolean;
  missingVars: {
    service: string;
    serviceDescription: string;
    variables: Array<{
      name: string;
      description: string;
      required: boolean;
    }>;
  }[];
}

/**
 * Validates environment variables and returns information about missing ones
 * @returns ValidationResult object with missing variables grouped by service
 */
export function validateEnvironment(env: Record<string, string | undefined> = import.meta.env): ValidationResult {
  const missingVars: ValidationResult['missingVars'] = [];
  let isValid = true;

  // Check each service and its variables
  Object.entries(environmentVariables).forEach(([service, serviceData]) => {
    const missingServiceVars: Array<{ name: string; description: string; required: boolean }> = [];

    // Only validate VITE_ vars on client (import.meta.env)
    Object.entries(serviceData.variables)
      .filter(([varName]) => varName.startsWith('VITE_'))
      .forEach(([varName, varData]) => {
      if (!env[varName]) {
        missingServiceVars.push({
          name: varName,
          description: varData.description,
          required: varData.required,
        });

        // Only set isValid to false if this is a required variable
        if (varData.required) {
          isValid = false;
        }
      }
    });

    if (missingServiceVars.length > 0) {
      missingVars.push({
        service,
        serviceDescription: serviceData.description,
        variables: missingServiceVars,
      });
    }
  });

  return { isValid, missingVars };
}

/**
 * Checks environment variables and logs warnings for missing ones
 * This function allows the application to continue running even with missing variables
 * @returns true if all required variables are present, false otherwise
 */
export function checkEnvironmentVariables(env: Record<string, string | undefined> = import.meta.env): boolean {
  const { isValid, missingVars } = validateEnvironment(env);

  if (missingVars.length > 0) {
    // Log warnings for missing variables
    console.warn("⚠️ Some environment variables are missing:");

    // Group by service for cleaner output
    missingVars.forEach(({ service, serviceDescription, variables }) => {
      // Group required and optional variables
      const requiredVars = variables.filter(v => v.required);
      const optionalVars = variables.filter(v => !v.required);
      
      if (requiredVars.length > 0) {
        console.warn(`🔴 ${service.toUpperCase()} (${serviceDescription}) - Required variables missing:`);
        requiredVars.forEach(v => {
          console.warn(`   - ${v.name}: ${v.description}`);
        });
      }
      
      if (optionalVars.length > 0) {
        console.warn(`🟡 ${service.toUpperCase()} (${serviceDescription}) - Optional variables missing:`);
        optionalVars.forEach(v => {
          console.warn(`   - ${v.name}: ${v.description}`);
        });
      }
    });

    // Important notification that the app will continue to load
    console.warn("ℹ️ The application will continue loading, but some features may not work correctly.");
  }

  return isValid;
}

// Export default object for easier imports
export default {
  validateEnvironment,
  checkEnvironmentVariables,
};